import React from 'react';
import styled from 'styled-components';
import { skills } from '../../data/constants';
import SectionHeading from '../common/SectionHeading';

const Section = styled.section`
  width: 100%;
  padding: 120px 24px;
  position: relative;
  overflow: hidden;
`;

const Glow = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 10% 20%, rgba(133, 76, 230, 0.25), transparent 55%),
    radial-gradient(circle at 80% 0%, rgba(0, 70, 209, 0.25), transparent 45%);
  opacity: 0.6;
  pointer-events: none;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 56px;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 360px) 1fr;
  gap: 32px;
  align-items: flex-start;
  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const OverviewPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 28px;
  border-radius: 28px;
  background: ${({ theme }) => theme.card_light};
  border: 1px solid rgba(133, 76, 230, 0.25);
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.35);
`;

const OverviewList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const OverviewItem = styled.li`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ theme }) => theme.primary};
  margin-top: 6px;
  flex-shrink: 0;
`;

const OverviewText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.6;
`;

const CapabilityGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CapabilityCard = styled.article`
  border-radius: 28px;
  padding: 24px 28px;
  background: rgba(20, 22, 35, 0.92);
  border: 1px solid rgba(133, 76, 230, 0.2);
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const CapabilityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 12px;
`;

const CapabilityTitle = styled.h3`
  margin: 0;
  font-size: 22px;
  color: ${({ theme }) => theme.text_primary};
`;

const CapabilityMeta = styled.span`
  font-size: 12px;
  letter-spacing: 0.2em;
  color: ${({ theme }) => theme.text_secondary};
  text-transform: uppercase;
`;

const CapabilityDesc = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.6;
`;

const ToolGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const ToolTag = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.02);
  color: ${({ theme }) => theme.text_secondary};
  font-size: 13px;
`;

const ToolIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const skillDescriptions = {
  'AI & ML': 'LLM orchestration, classical ML, experimentation ops, and applied research.',
  'Web Development':
    'Designing performant interfaces, systems APIs, and full-stack workflows that scale.',
  'App Development': 'Building cross-platform experiences with native feel and secure data flows.',
  Others: 'Complementary tools and platforms that keep delivery smooth end-to-end.',
};

const overviewBullets = [
  'GenAI ops · RAG pipelines · Experiment tracking frameworks',
  'Designing APIs, frontends, and mobile experiences in tandem',
  'Trusted partner for product, design, and data leadership squads',
];

const Skills = () => (
  <Section id="skills">
    <Glow />
    <Content>
      <Layout>
        <OverviewPanel>
          <SectionHeading
            eyebrow="Skillset"
            title="A toolkit built across AI, product, & engineering"
            description="Four years of hands-on work across research labs, consulting teams, and in‑house product squads."
            align="left"
          />
          <OverviewList>
            {overviewBullets.map((item) => (
              <OverviewItem key={item}>
                <Dot />
                <OverviewText>{item}</OverviewText>
              </OverviewItem>
            ))}
          </OverviewList>
        </OverviewPanel>
        <CapabilityGrid>
          {skills.map((skill) => (
            <CapabilityCard key={skill.title}>
              <CapabilityHeader>
                <CapabilityTitle>{skill.title}</CapabilityTitle>
                <CapabilityMeta>{skill.skills.length} TOOLS</CapabilityMeta>
              </CapabilityHeader>
              <CapabilityDesc>{skillDescriptions[skill.title] || ''}</CapabilityDesc>
              <ToolGrid>
                {skill.skills.map((item) => (
                  <ToolTag key={item.name}>
                    <ToolIcon src={item.image} alt={item.name} />
                    {item.name}
                  </ToolTag>
                ))}
              </ToolGrid>
            </CapabilityCard>
          ))}
        </CapabilityGrid>
      </Layout>
    </Content>
  </Section>
);

export default Skills;